import { useMemo } from 'react';
import { EChart as ReactECharts } from '../../charts/EChart';
import { useAppContext } from '../../../providers/AppProvider';
import { formatLength, toKm } from '../../../helpers/formatLength';

/**
 * Every metre the factory was ever asked for, and what became of it.
 *
 * The interesting number is not how much was ordered — it is the shape of the
 * split. A book that is 80% made is a factory keeping up; the same book 80%
 * "promised" is a factory that has committed everything and made nothing, and
 * the two look identical on any chart that only plots order volume.
 *
 * Ordered by how final each fate is, left to right, so the ring reads as a
 * pipeline rather than five unrelated slices.
 */

const FATES = [
  { key: 'produced', name: 'Made', color: 'success' },
  { key: 'stock', name: 'From stock', color: 'primary' },
  { key: 'promised', name: 'Promised', color: 'info' },
  { key: 'open', name: 'Nothing yet', color: 'warning' },
  { key: 'cancelled', name: 'Cancelled', color: 'danger' }
];

const MetreFateChart = ({ fate = {}, style }) => {
  const { getThemeColor } = useAppContext();

  const data = useMemo(
    () =>
      FATES.map(f => ({
        name: f.name,
        value: toKm(fate[f.key]),
        meters: Number(fate[f.key]) || 0
      })).filter(d => d.meters > 0),
    [fate]
  );

  const total = useMemo(
    () => FATES.reduce((s, f) => s + (Number(fate[f.key]) || 0), 0),
    [fate]
  );

  const option = useMemo(
    () => ({
      color: FATES.filter(f => (Number(fate[f.key]) || 0) > 0).map(f =>
        getThemeColor(f.color)
      ),
      tooltip: {
        trigger: 'item',
        padding: [8, 12],
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-300'),
        borderWidth: 1,
        textStyle: { color: getThemeColor('dark'), fontFamily: 'Nunito Sans' },
        formatter: p =>
          `<strong>${p?.name || ''}</strong><br/>${formatLength(
            p?.data?.meters
          )} · ${p?.percent ?? 0}%`
      },
      legend: {
        bottom: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: getThemeColor('gray-800'),
          fontFamily: 'Nunito Sans',
          fontWeight: 600
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['46%', '74%'],
          center: ['50%', '44%'],
          avoidLabelOverlap: true,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: {
            borderColor: getThemeColor('body-bg'),
            borderWidth: 4
          },
          emphasis: {
            scale: true,
            scaleSize: 4,
            label: {
              show: true,
              formatter: '{b}\n{d}%',
              fontWeight: 700,
              fontSize: 13,
              color: getThemeColor('gray-900')
            }
          },
          data
        }
      ],
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            text: formatLength(total),
            textAlign: 'center',
            fill: getThemeColor('gray-1100'),
            fontSize: 24,
            fontWeight: 800,
            fontFamily: 'Nunito Sans'
          }
        },
        {
          type: 'text',
          left: 'center',
          top: '45%',
          style: {
            text: 'ever ordered',
            textAlign: 'center',
            fill: getThemeColor('gray-700'),
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'Nunito Sans'
          }
        }
      ]
    }),
    [data, fate, total, getThemeColor]
  );

  if (total <= 0) {
    return (
      <div className="text-center text-body-tertiary py-6 fs-9">
        No metres in this period.
      </div>
    );
  }

  return <ReactECharts option={option} style={style} />;
};

export default MetreFateChart;
